import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faTimes, faCheck } from "@fortawesome/free-solid-svg-icons";
import { Notification, useNotifications } from "../../contexts/NotificationsContext";
import {
  getActorInitial,
  getContentThumbnail,
  getNotificationTypeMeta,
  getThumbnailAlt,
  isActorNotification,
  timeAgo,
} from "../../utils/notifications";

function NotificationIcon({ type, className }: { type: Notification["type"]; className?: string }) {
  const { icon, color } = getNotificationTypeMeta(type);
  return <FontAwesomeIcon icon={icon} className={`${className || "text-sm"} ${color}`} />;
}

function SkeletonRow() {
  return (
    <div className="flex gap-3 pl-5 pr-3 py-3 border-b border-theme-border animate-pulse">
      <div className="w-9 h-9 rounded-full bg-theme-surface shrink-0" />
      <div className="flex-1 space-y-2 py-1">
        <div className="h-3 bg-theme-surface w-11/12" />
        <div className="h-3 bg-theme-surface w-2/3" />
      </div>
      <div className="w-16 rounded-[3px] bg-theme-surface shrink-0" style={{ aspectRatio: "16 / 9" }} />
    </div>
  );
}

type Props = { onClose: () => void };

export default function NotificationDropdown({ onClose }: Props) {
  const router = useRouter();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, deleteNotification, refresh } =
    useNotifications();
  const ref = useRef<HTMLDivElement>(null);

  // Refresh on open so the panel is never stale between polls
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleNotificationClick = (n: Notification) => {
    if (!n.isRead) markAsRead(n._id);
    onClose();
    if (n.link) router.push(n.link);
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Notifications"
      className="absolute top-10 right-0 w-[400px] max-w-[calc(100vw-1.5rem)] max-h-[480px] bg-theme-card rounded-[4px] shadow-float z-50 flex flex-col overflow-hidden whitespace-normal text-left text-theme-text"
    >
      {/* Header: the panel's title plate (stage navy in both themes) */}
      <div className="flex items-center justify-between gap-3 bg-stage-deep px-4 py-2.5 shrink-0">
        <div className="flex items-center gap-2">
          <span className="font-display text-lg font-extrabold italic text-white">Notifications</span>
          {unreadCount > 0 && (
            <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center bg-signal px-1.5 font-display text-[13px] font-semibold tabular-nums text-white">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="flex items-center gap-1.5 font-display text-[13px] font-semibold text-[#8DB4FF] transition-colors hover:text-white"
          >
            <FontAwesomeIcon icon={faCheck} className="text-xs" />
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1">
        {loading && notifications.length === 0 ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-theme-text-secondary gap-2">
            <span className="mb-1 flex h-12 w-12 items-center justify-center bg-theme-surface">
              <FontAwesomeIcon icon={faBell} className="text-xl" />
            </span>
            <span className="text-sm font-semibold text-theme-text">No notifications yet</span>
            <span className="text-[13px] text-center">
              Replies, likes and updates for games in your watchlist will show up here.
            </span>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n._id}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleNotificationClick(n);
                }
              }}
              className={`group relative flex gap-3 pl-5 pr-3 py-3 cursor-pointer hover:bg-theme-surface transition-colors border-b border-theme-border focus-visible:outline-none focus-visible:bg-theme-surface ${
                !n.isRead ? "bg-brand-main-100 dark:bg-[#0F2A63] dark:hover:bg-theme-surface dark:focus-visible:bg-theme-surface" : ""
              }`}
              onClick={() => handleNotificationClick(n)}
            >
              {/* Unread: a small signal-blue square in the left gutter (no
                  coloured side bar) */}
              {!n.isRead && (
                <span className="absolute left-2 top-[1.15rem] h-1.5 w-1.5 bg-signal dark:bg-[#8DB4FF]" aria-hidden="true" />
              )}

              {/* Actor avatar — only for notifications a person triggered.
                  Content updates have no actor, so they get no leading slot. */}
              {isActorNotification(n) && (
                <div className="shrink-0 mt-0.5 relative">
                  {n.triggeredByPicture ? (
                    <div className="relative w-9 h-9 rounded-full overflow-hidden ring-2 ring-theme-border">
                      <Image
                        src={n.triggeredByPicture}
                        fill
                        sizes="36px"
                        alt={n.triggeredByName || ""}
                        className="object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-theme-surface flex items-center justify-center">
                      <span className="text-sm font-semibold text-theme-text-secondary">
                        {getActorInitial(n)}
                      </span>
                    </div>
                  )}
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-theme-card flex items-center justify-center shadow-sm ring-1 ring-theme-border">
                    <NotificationIcon type={n.type} className="text-[9px]" />
                  </div>
                </div>
              )}

              {/* Message */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-theme-text leading-snug line-clamp-2">
                  {n.message}
                </p>
                <p className="text-xs text-theme-text-secondary mt-1 tabular-nums">
                  {getNotificationTypeMeta(n.type).label} &middot; {timeAgo(n.createdAt)}
                </p>
              </div>

              {/* 16:9 content preview */}
              {getContentThumbnail(n) && (
                <div
                  className="relative w-16 shrink-0 self-start mt-0.5 rounded-[3px] overflow-hidden bg-theme-surface"
                  style={{ aspectRatio: "16 / 9" }}
                >
                  <Image
                    src={getContentThumbnail(n)}
                    fill
                    sizes="64px"
                    alt={getThumbnailAlt(n)}
                    className="object-cover"
                  />
                </div>
              )}

              {/* Delete */}
              <button
                onClick={(e) => { e.stopPropagation(); deleteNotification(n._id); }}
                className="shrink-0 self-start -mr-1 flex h-6 w-6 items-center justify-center rounded-[2px] text-theme-text-secondary opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 hover:bg-theme-card hover:text-theme-text transition-all"
                aria-label="Delete notification"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <Link
          href="/profile?tab=notifications"
          onClick={onClose}
          className="block text-center font-display text-sm font-semibold text-signal dark:text-[#8DB4FF] px-4 py-2.5 border-t border-theme-border bg-theme-card hover:bg-theme-surface transition-colors shrink-0"
        >
          View all notifications
        </Link>
      )}
    </div>
  );
}
